import { useState, useEffect, useRef, useCallback } from "react";
import { Upload, ChevronLeft, Info, Loader2, Check } from "lucide-react";
import { HexColorPicker } from "react-colorful";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { getClubLogoUrl } from "@/lib/bfv";

import type { WizardState } from "./types";

interface Props {
  state: WizardState;
  onNext: (updates: Partial<WizardState>) => void;
  onBack: () => void;
}

function rgbToHex(r: number, g: number, b: number) {
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

type LogoColor = { r: number; g: number; b: number; n: number };

function isStrongColor({ r, g, b, n }: LogoColor) {
  r /= n; g /= n; b /= n;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const saturation = max === 0 ? 0 : (max - min) / max;
  const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return saturation >= 0.3 && brightness >= 0.12 && brightness <= 0.85;
}


const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export default function StepBranding({ state, onNext, onBack }: Props) {
  const bfvLogoUrl = getClubLogoUrl(state.club);
  const [uploadedLogo, setUploadedLogo] = useState<string | null>(
    state.branding.logoDataUrl?.startsWith("data:") ? state.branding.logoDataUrl : null,
  );
  const [proxiedBfvLogo, setProxiedBfvLogo] = useState<string | null>(null);
  const [loadingLogo, setLoadingLogo] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(state.branding.primaryColor);
  const [detected, setDetected] = useState<string[]>([]);
  const [primaryInput, setPrimaryInput] = useState(primaryColor);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingColors, setLoadingColors] = useState(false);

  // Returning from the next step must not replace the chosen color with auto-detection.
  const userTouched = useRef(Boolean(state.branding.logoDataUrl));
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentLogo = uploadedLogo || proxiedBfvLogo;

  useEffect(() => {
    if (uploadedLogo || !bfvLogoUrl) return;
    let cancelled = false;
    setLoadingLogo(true);
    (async () => {
      try {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-logo?url=${encodeURIComponent(bfvLogoUrl)}`;
        const res = await fetch(url, {
          headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
        });
        if (!res.ok) throw new Error(`Logo konnte nicht geladen werden (${res.status})`);
        const json = await res.json();
        if (!json.dataUrl) throw new Error("Logo konnte nicht geladen werden");
        if (!cancelled) setProxiedBfvLogo(json.dataUrl);
      } catch (e) {
        console.error("BFV logo proxy failed:", e);
        if (!cancelled) setProxiedBfvLogo(bfvLogoUrl);
      } finally {
        if (!cancelled) setLoadingLogo(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bfvLogoUrl, uploadedLogo]);

  const extractColors = useCallback(async () => {
    const img = imgRef.current;
    if (!img || !img.complete || img.naturalWidth === 0) return;
    setLoadingColors(true);
    try {
      // Eigene Analyse über ein Canvas: echte Pixelfarben zählen statt zu mischen.
      const size = 160;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, size, size);
      const { data } = ctx.getImageData(0, 0, size, size);

      const buckets = new Map<string, LogoColor>();
      let opaquePixels = 0;
      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3];
        if (a < 200) continue;
        opaquePixels++;
        const r = data[i], g = data[i + 1], b = data[i + 2];
        const key = `${r >> 4}-${g >> 4}-${b >> 4}`;
        const bucket = buckets.get(key) ?? { r: 0, g: 0, b: 0, n: 0 };
        bucket.r += r; bucket.g += g; bucket.b += b; bucket.n += 1;
        buckets.set(key, bucket);
      }

      // Merge near-identical shades and anti-aliased edges into recognizable logo colors.
      const groups: LogoColor[] = [];
      for (const bucket of [...buckets.values()].sort((a, b) => b.n - a.n)) {
        if (bucket.n < Math.max(3, opaquePixels * 0.001)) continue;
        const r = bucket.r / bucket.n, g = bucket.g / bucket.n, b = bucket.b / bucket.n;
        const match = groups.find((group) => {
          const distance = Math.hypot(r - group.r / group.n, g - group.g / group.n, b - group.b / group.n);
          return distance < 52;
        });
        if (match) {
          match.r += bucket.r; match.g += bucket.g; match.b += bucket.b; match.n += bucket.n;
        } else {
          groups.push({ ...bucket });
        }
      }

      const palette = groups
        .filter((group) => group.n >= Math.max(8, opaquePixels * 0.008))
        .map((group) => ({
          hex: rgbToHex(Math.round(group.r / group.n), Math.round(group.g / group.n), Math.round(group.b / group.n)),
          count: group.n,
          strong: isStrongColor(group),
        }))
        .sort((a, b) => Number(b.strong) - Number(a.strong) || b.count - a.count)
        .slice(0, 12);

      setDetected(palette.map((color) => color.hex));
      if (!userTouched.current && palette.length) {
        const primary = palette[0].hex;
        setPrimaryColor(primary);
        setPrimaryInput(primary);
      }
    } catch (e) {
      console.error("color extraction failed", e);
      setDetected([]);
    } finally {
      setLoadingColors(false);
    }
  }, []);

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) extractColors();
  }, [currentLogo, extractColors]);

  const handleFile = (file: File) => {
    if (file.size > 2 * 1024 * 1024 || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedLogo(reader.result as string);
      userTouched.current = false;
      setDetected([]);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const updatePrimary = (hex: string) => {
    userTouched.current = true;
    setPrimaryInput(hex);
    if (HEX_RE.test(hex)) setPrimaryColor(hex);
  };

  const selectDetectedColor = (hex: string) => {
    userTouched.current = true;
    setPrimaryColor(hex);
    setPrimaryInput(hex);
  };

  const handleNext = () => {
    onNext({
      branding: {
        // Das Proxy-Bild dient nur der Farbanalyse. Für Vorschauen und die
        // dauerhafte Speicherung muss die kurze, zum Verein gehörende BFV-URL
        // verwendet werden, damit der Grafikdienst kein fremdes Standardwappen zeigt.
        logoDataUrl: uploadedLogo || bfvLogoUrl || null,
        primaryColor,
        secondaryColor: "#FFFFFF",
      },
    });
  };


  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-foreground">
          Dein Vereins-Branding
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Logo wurde automatisch vom BFV übernommen. Du kannst auch ein eigenes hochladen.
        </p>
      </div>

      {/* Logo */}
      <div className="grid gap-4 sm:grid-cols-[160px,1fr]">
        <div className="relative flex h-40 w-40 items-center justify-center rounded-xl border border-border bg-white overflow-hidden shrink-0">
          {loadingLogo && !currentLogo && (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          )}
          {currentLogo && (
            <img
              ref={imgRef}
              src={currentLogo}
              alt="Vereinslogo"
              crossOrigin="anonymous"
              className="h-full w-full object-contain p-3"
              onLoad={extractColors}
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          )}
          {!currentLogo && !loadingLogo && (
            <span className="text-5xl text-muted-foreground">⚽</span>
          )}
        </div>

        <div
          className="flex flex-col justify-center cursor-pointer rounded-xl border-2 border-dashed border-border bg-muted/30 p-6 text-center transition-colors hover:border-primary/40 hover:bg-muted/50"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="mx-auto h-7 w-7 text-muted-foreground" />
          <p className="mt-2 text-sm font-medium text-foreground">Eigenes Logo hochladen</p>
          <p className="text-xs text-muted-foreground">PNG oder JPG, max. 2 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
        </div>
      </div>

      {/* Vereinsfarben */}
      <div className="space-y-4">
        <div>
          <Label className="text-base">Vereinsfarbe</Label>
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
            <Info className="h-4 w-4 shrink-0 text-primary mt-0.5" />
            <p className="text-xs text-foreground leading-relaxed">
              Wähle eine Farbe aus deinem Logo als Hauptfarbe. Kräftige Farben wie Blau oder Grün funktionieren besonders gut mit Weiß und Schwarz. Du kannst die Farbe auch manuell anpassen.
            </p>
          </div>
        </div>

        {loadingColors && (
          <p className="text-xs text-muted-foreground">Farben werden analysiert…</p>
        )}

        {/* Logo palette: one selected primary color, all significant logo colors available. */}
        {detected.length > 0 && (
          <div className="border-y border-border py-4">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Farben aus deinem Logo
            </p>
            <div className="flex flex-wrap gap-3">
              {detected.map((hex) => {
                const selected = primaryColor.toLowerCase() === hex.toLowerCase();
                const rgb = [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16));
                const dark = 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2] < 135;
                return (
                  <Button
                    key={hex}
                    type="button"
                    variant="ghost"
                    onClick={() => selectDetectedColor(hex)}
                    aria-label={`Logofarbe ${hex.toUpperCase()} wählen`}
                    aria-pressed={selected}
                    className={`h-auto min-w-16 flex-col gap-1.5 rounded-md border p-2 hover:bg-muted/50 ${selected ? "border-primary ring-2 ring-primary/30" : "border-border"}`}
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-sm border border-border" style={{ backgroundColor: hex }}>
                      {selected && <Check className={`h-5 w-5 ${dark ? "text-background" : "text-foreground"}`} strokeWidth={3} />}
                    </span>
                    <span className="font-mono text-xs text-foreground">{hex.toUpperCase()}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {/* Manual override */}
        <div>
          <Label className="text-sm">Hauptfarbe (Hex)</Label>
          <div className="mt-1.5 flex gap-2">
            <button
              type="button"
              onClick={() => setPickerOpen(!pickerOpen)}
              className="h-10 w-10 shrink-0 rounded-md border border-input"
              style={{ backgroundColor: primaryColor }}
              aria-label="Farbwähler öffnen"
            />
            <Input
              value={primaryInput}
              onChange={(e) => updatePrimary(e.target.value)}
              placeholder="#1B5EAB"
              className="font-mono"
              maxLength={7}
            />
          </div>
          {pickerOpen && (
            <div className="mt-2 rounded-xl border border-border bg-card p-3 shadow-lg">
              <HexColorPicker
                color={primaryColor}
                onChange={(c) => {
                  userTouched.current = true;
                  setPrimaryColor(c);
                  setPrimaryInput(c);
                }}
              />
            </div>
          )}
        </div>
      </div>


      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-1">
          <ChevronLeft className="h-4 w-4" /> Zurück
        </Button>
        <Button variant="hero" className="flex-1" onClick={handleNext}>
          Weiter →
        </Button>
      </div>
    </div>
  );
}
